// One placement as a section of the survey page (redesign spec §7): its
// link, QR and settings read-only until Edit, then its own form, saved on
// its own. An inline placement also shows its embed reference, with the
// snippets behind an Embed code dialog; a link placement shows its id, the
// invite URL scheme and the counts, with the mail markup behind an Email
// block dialog (email spec §8.1). Destructive actions — a new link, deleting
// the placement — sit in the danger strip while editing; one with responses
// is deactivated instead.

import { Badge, Button, Confirm, CopyButton, PropertyPanel, toast } from "@pej/kore/components";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ApiError, isConstraintViolation } from "@/api/error";
import { placementKeys } from "@/api/keys";
import { useCreatePlacement, useDeletePlacement, usePatchPlacement, useRotateToken } from "@/api/placements";
import type { PlacementRow } from "@/api/types";
import { toastFormError, toastMutationError } from "@/components/error-toast";
import { DangerStrip } from "@/components/section-page/danger-zone";
import { SectionCard } from "@/components/section-page/section-card";
import { useSectionEdit } from "@/components/section-page/section-edit";
import { SECTION } from "@/features/surveys/survey-sections";
import { Form, useKoreForm } from "@/lib/forms";
import { countOf } from "@/lib/plural";
import { EmailBlockDialog } from "./email-block-dialog";
import { EmbedCodeDialog } from "./embed-code-dialog";
import { LinkPlacementView } from "./link-placement-view";
import {
    MODE_LABELS,
    NEW_PLACEMENT_DEFAULTS,
    PlacementFields,
    type PlacementFormValues,
    placementFormSchema,
    revisitLabel,
    toPlacementBody,
    toPlacementDefaults,
} from "./placement-fields";
import { PlacementLink } from "./placement-link";

export interface PlacementSectionProps {
    workspaceId: string;
    surveyId: string;
    /** null for the draft "New placement". */
    placement: PlacementRow | null;
    onDraftDone?: () => void;
}

export function PlacementSection({ workspaceId, surveyId, placement, onDraftDone }: PlacementSectionProps) {
    const isDraft = placement === null;
    const placementId = placement?.id ?? "";
    const createPlacement = useCreatePlacement(workspaceId, surveyId);
    const patchPlacement = usePatchPlacement(surveyId, placementId);
    const deletePlacement = useDeletePlacement(workspaceId, surveyId, placementId);
    const rotateToken = useRotateToken(surveyId, placementId);
    const [confirm, setConfirm] = useState<"rotate" | "delete" | null>(null);
    const queryClient = useQueryClient();

    const defaults = placement ? toPlacementDefaults(placement) : NEW_PLACEMENT_DEFAULTS;
    const form = useKoreForm({ schema: placementFormSchema, defaultValues: defaults });
    const sectionId = isDraft ? SECTION.newPlacement : SECTION.placement(placement.id);
    // From the SAVED placement, never the form: useSectionEdit re-registers
    // when the title changes, and re-registering drops the section out of
    // edit mode — a live title would end the edit on the first keystroke.
    const title = placement?.name ?? "New placement";
    const edit = useSectionEdit({
        sectionId,
        sectionTitle: title,
        isDirty: isDraft || form.formState.isDirty,
        hasErrors: Object.keys(form.formState.errors).length > 0,
        reset: () => {
            form.reset(defaults);
            if (isDraft) onDraftDone?.();
        },
    });

    // Saved data moved on (this card's save, a new link) — follow it while
    // not editing, so the next Edit starts from what is stored.
    const savedKey = JSON.stringify(defaults);
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the saved values themselves
    useEffect(() => {
        if (!edit.isEditing) form.reset(defaults);
    }, [savedKey, edit.isEditing]);

    const cardRef = useRef<HTMLElement>(null);
    // A draft opens in edit mode, and is brought into view: it's added at the
    // end of its list, often below the fold. scroll-mt on the section lands it
    // clear of the compact header.
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
    useEffect(() => {
        if (!isDraft) return;
        edit.enterEdit();
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    async function onSubmit(values: PlacementFormValues) {
        const body = toPlacementBody(values);
        try {
            if (isDraft) {
                await createPlacement.mutateAsync({ ...body, name: values.name });
                toast.success("Placement added");
            } else {
                await patchPlacement.mutateAsync(body);
                toast.success("Placement saved");
            }
            edit.exitEdit();
            if (isDraft) onDraftDone?.();
        } catch (err) {
            if (err instanceof ApiError) {
                toastFormError(isDraft ? "Create failed" : "Save failed", form, err);
                return;
            }
            throw err;
        }
    }

    function cancel() {
        edit.exitEdit();
        if (isDraft) onDraftDone?.();
    }

    async function rotate() {
        try {
            await rotateToken.mutateAsync();
            toast.success("New link and QR code generated");
            setConfirm(null);
        } catch (err) {
            toastMutationError("New link failed", err);
        }
    }

    async function remove() {
        try {
            await deletePlacement.mutateAsync();
            toast.success("Placement deleted");
            setConfirm(null);
        } catch (err) {
            // A constraint violation here means a response landed since the
            // list was loaded. The mutation has refreshed the list by now, so
            // the card shows the refusal with Delete disabled, and the toast
            // says the same: from the refusal's own count, else the list's.
            setConfirm(null);
            if (!isConstraintViolation(err)) {
                toastMutationError("Delete failed", err);
                return;
            }
            const fresh = queryClient
                .getQueryData<PlacementRow[]>(placementKeys.list(surveyId))
                ?.find((row) => row.id === placement?.id);
            const responseCount = err.countParam("responseCount") ?? fresh?.responseCount ?? 0;
            toast.error(responseCount > 0 ? placementDeleteRefusal(responseCount) : "Failed to delete the placement");
        }
    }

    const saving = isDraft ? createPlacement.isPending : patchPlacement.isPending;
    const actions = edit.isEditing ? (
        <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={cancel}>
                Cancel
            </Button>
            <Button
                type="button"
                size="sm"
                disabled={saving || (!isDraft && !form.formState.isDirty)}
                onClick={() => void form.handleSubmit(onSubmit)()}
            >
                Save
            </Button>
        </div>
    ) : edit.isReadOnly ? null : (
        <Button type="button" variant="outline" size="sm" onClick={edit.enterEdit}>
            <Pencil aria-hidden="true" />
            Edit
        </Button>
    );

    const responseCount = placement?.responseCount ?? 0;

    return (
        <SectionCard ref={cardRef} id={sectionId} title={title} actions={actions}>
            {edit.isEditing ? (
                <>
                    <Form form={form} onSubmit={onSubmit}>
                        <PlacementFields isDraft={isDraft} />
                    </Form>
                    {placement && (
                        <DangerStrip isEditing={edit.isEditing}>
                            {placement.mode !== "link" && (
                                <Button type="button" variant="outline" size="sm" onClick={() => setConfirm("rotate")}>
                                    New link
                                </Button>
                            )}
                            {responseCount > 0 && (
                                <p className="text-sm text-kore-text-muted">{placementDeleteRefusal(responseCount)}</p>
                            )}
                            <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                disabled={responseCount > 0}
                                onClick={() => setConfirm("delete")}
                            >
                                Delete placement
                            </Button>
                        </DangerStrip>
                    )}
                </>
            ) : (
                placement && <PlacementView placement={placement} />
            )}
            {placement && (
                <>
                    <Confirm
                        open={confirm === "rotate"}
                        onOpenChange={(open) => setConfirm(open ? "rotate" : null)}
                        title="Rotate this placement's link?"
                        description="The old link and QR stop working immediately."
                        primaryLabel={rotateToken.isPending ? "Rotating…" : "New link"}
                        variant="destructive"
                        confirming={rotateToken.isPending}
                        onConfirm={rotate}
                    />
                    <Confirm
                        open={confirm === "delete"}
                        onOpenChange={(open) => setConfirm(open ? "delete" : null)}
                        title={`Delete ${placement.name}?`}
                        description={
                            placement.mode === "link"
                                ? "Every invite sent for it stops working."
                                : "Its link, QR code and any embeds stop working."
                        }
                        primaryLabel={deletePlacement.isPending ? "Deleting…" : "Delete placement"}
                        variant="destructive"
                        confirming={deletePlacement.isPending}
                        onConfirm={remove}
                    />
                </>
            )}
        </SectionCard>
    );
}

/** Spec §7's refusal for a placement that has responses. */
function placementDeleteRefusal(responseCount: number): string {
    return `This placement has ${countOf(responseCount, "response")}. Deactivate it instead.`;
}

function PlacementView({ placement }: { placement: PlacementRow }) {
    const [embedOpen, setEmbedOpen] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const token = placement.publicToken;
    const link = placement.mode === "link";
    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
                <Badge tone="neutral">{MODE_LABELS[placement.mode]}</Badge>
                {!placement.active && <Badge tone="neutral">Inactive</Badge>}
            </div>
            {link ? (
                <LinkPlacementView placement={placement} onOpenEmailBlock={() => setEmailOpen(true)} />
            ) : (
                <PlacementLink placement={placement} showQr />
            )}
            <PropertyPanel>
                {link ? (
                    <PropertyPanel.Field label="Invites expire after">
                        {placement.inviteTtlDays} days
                    </PropertyPanel.Field>
                ) : placement.mode === "kiosk" ? (
                    <>
                        <PropertyPanel.Field label="Attract screen">
                            {placement.attractEnabled
                                ? placement.attractText
                                    ? `On · ${placement.attractText}`
                                    : "On"
                                : "Off"}
                        </PropertyPanel.Field>
                        <PropertyPanel.Field label="Reset after">{placement.resetSeconds} s</PropertyPanel.Field>
                        <PropertyPanel.Field label="Thank-you screen">{placement.thanksSeconds} s</PropertyPanel.Field>
                    </>
                ) : (
                    <PropertyPanel.Field label="On revisit">{revisitLabel(placement.revisit)}</PropertyPanel.Field>
                )}
                {placement.locationRef && (
                    <PropertyPanel.Field label="Location reference">{placement.locationRef}</PropertyPanel.Field>
                )}
            </PropertyPanel>
            {placement.mode === "inline" && (
                <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium">Embed reference</p>
                    <code className="rounded-kore-md bg-kore-bg px-2 py-1 text-sm">{token}</code>
                    <CopyButton value={token} label="Copy embed reference" />
                    <Button type="button" variant="outline" size="sm" onClick={() => setEmbedOpen(true)}>
                        Embed code
                    </Button>
                    <EmbedCodeDialog name={placement.name} token={token} open={embedOpen} onOpenChange={setEmbedOpen} />
                </div>
            )}
            {link && (
                <EmailBlockDialog
                    name={placement.name}
                    placementId={placement.id}
                    open={emailOpen}
                    onOpenChange={setEmailOpen}
                />
            )}
        </div>
    );
}
